import { sqrDistanceBetweenPoints } from './geometry';
import { hasFrameMemberBehaviors, isFrameLike } from '../objects/guards';
import type { fabric } from 'fabric';
import * as zoomUtils from './zoom';
import type WhiteboardController from '../controllers/WhiteboardController';

/**
 * SelectionCycleManager.ts
 *
 * Gives the next/previous item to navigate to when tabbing through shapes on the whiteboard.
 *
 * Key features:
 * - Maintains a cycle order of selectable objects for consistent navigation
 * - Uses a combination of hierarchical and spatial ordering. Hierarchical ordering
 *   ensures frames and their contents are treated as units, while spatial ordering
 *   ensures objects are ordered by proximity to the current selection.
 * - Zooms to selected object on cycle
 *
 * The primary algorithm uses a "frame-first" approach where:
 * 1. Frames are treated as distinct units along with their contents
 * 2. Top-level objects (frames and standalone objects) are ordered by proximity to current selection
 * 3. Within frames, objects are ordered primarily top-to-bottom, then left-to-right
 * 4. The cycle is always initialized with the current object as the starting point
 */

interface FrameGroup {
  frame: fabric.Object;
  children: fabric.Object[];
  center: fabric.Point;
}

interface TopLevelElement {
  type: 'frame' | 'object';
  element: FrameGroup | fabric.Object;
  distance: number;
  center: fabric.Point;
}

export default class SelectionCycleManager {
  // Tab navigation state
  private lastObjectCount = 0;
  private lastTabSelectedId: string | null = null;
  private cycleOrder: string[] = [];

  constructor(private readonly whiteboardController: WhiteboardController) {}

  private get selectableObjects(): fabric.Object[] {
    return (
      this.whiteboardController.canvas._objects?.filter(
        obj => obj.selectable && obj.visible
      ) || []
    );
  }

  /**
   * Cycles selection to the next or previous object based on the current selection
   */
  cycleSelection(forward = true, currentSelection: fabric.Object) {
    const selectableObjects = this.selectableObjects;
    if (selectableObjects.length <= 1) return;

    const currentObject = this.getCurrentObject(currentSelection);
    const currentId = currentObject.id;

    if (this.shouldResetCycleState(currentId, selectableObjects.length)) {
      this.initializeHierarchicalCycleOrder(selectableObjects, currentObject);
    }

    // Get the current index in the cycle order
    const currentIndex = this.cycleOrder.indexOf(currentId);

    // Extra guard against collaborative changes.
    if (currentIndex === -1) {
      this.initializeHierarchicalCycleOrder(selectableObjects, currentObject);
      return;
    }

    // Calculate next index with proper wrapping for both directions
    const nextIndex = this.getNextIndex(currentIndex, forward);

    // Make sure we have a different object
    const finalNextId = this.cycleOrder[nextIndex];
    const nextObject = selectableObjects.find(obj => obj.id === finalNextId);

    if (nextObject && nextObject !== currentObject) {
      this.lastTabSelectedId = finalNextId;
      this.whiteboardController.canvas.replaceSelectionWith?.(nextObject);
      this.ensureObjectVisible(nextObject);
    }
  }

  private getCurrentObject(currentSelection: fabric.Object): fabric.Object {
    return currentSelection.type === 'activeSelection'
      ? currentSelection.getObjects()[0]
      : currentSelection;
  }

  /**
   * Calculates the next index in the cycle order, handling wrapping
   */
  private getNextIndex(currentIndex: number, forward: boolean): number {
    const length = this.cycleOrder.length;
    const offset = forward ? 1 : -1;

    // Calculate next index with wrapping
    let nextIndex = this.getModularIndex(currentIndex, offset, length);

    const currentId = this.cycleOrder[currentIndex];
    const nextId = this.cycleOrder[nextIndex];

    // If we'd select the same object, move one more step
    if (nextId === currentId && length > 1) {
      nextIndex = this.getModularIndex(nextIndex, offset, length);
    }

    return nextIndex;
  }

  /**
   * Helper function to calculate modular index with wrapping
   */
  private getModularIndex(
    index: number,
    offset: number,
    length: number
  ): number {
    return (index + offset + length) % length;
  }

  /**
   * Ensures the object is visible in the viewport
   */
  private ensureObjectVisible(obj: fabric.Object): void {
    // Check if the selected object is visible in the viewport
    if (!obj.isOnScreen()) {
      // Center the object in the viewport using zoom utils
      zoomUtils.zoomAndPanTo(this.whiteboardController, obj, {
        animate: false,
        // Don't change zoom level, just pan to make object visible
        maxZoom: this.whiteboardController.canvas.getZoom(),
        marginFactor: 0.9,
      });
    }
  }

  private initializeHierarchicalCycleOrder(
    selectableObjects: fabric.Object[],
    currentObject: fabric.Object
  ): void {
    this.cycleOrder = this.buildFrameFirstOrder(
      selectableObjects,
      currentObject
    );
    this.ensureCurrentObjectIsFirstInOrder(currentObject.id);
    this.lastObjectCount = selectableObjects.length;
    this.lastTabSelectedId = currentObject.id;
  }

  private ensureCurrentObjectIsFirstInOrder(currentId: string): void {
    // Make sure current object is first in the order
    if (this.cycleOrder[0] !== currentId) {
      // Remove current object from wherever it is in the order
      const currentIndex = this.cycleOrder.indexOf(currentId);
      if (currentIndex > 0) {
        this.cycleOrder.splice(currentIndex, 1);
      }
      // Add it to the beginning
      this.cycleOrder.unshift(currentId);
    }

    // Ensure we don't select the same object again
    if (
      this.cycleOrder.length > 1 &&
      this.cycleOrder[0] === this.cycleOrder[1]
    ) {
      this.cycleOrder.splice(1, 1);
    }
  }

  /**
   * Determines if the cycle state should be reset based on changes to selection or object count
   */
  private shouldResetCycleState(
    currentId: string,
    objectCount: number
  ): boolean {
    return (
      !this.cycleOrder ||
      this.lastTabSelectedId !== currentId ||
      this.lastObjectCount !== objectCount
    );
  }

  /**
   * Builds frame-first order prioritizing spatial proximity
   */
  private buildFrameFirstOrder(
    objects: fabric.Object[],
    currentObject: fabric.Object
  ): string[] {
    const frames = objects.filter(obj => isFrameLike(obj));
    const frameGroups = this.buildFrameHierarchy(frames, objects);
    const rootFrameGroups = this.filterRootFrameGroups(frameGroups, frames);
    const topLevelObjects = this.getTopLevelObjects(objects, frames);

    const topLevelElements = this.createTopLevelElements(
      rootFrameGroups,
      topLevelObjects,
      currentObject.id,
      currentObject.getCenterPoint()
    );

    return this.buildFinalOrder(topLevelElements, currentObject.id);
  }

  private buildFrameHierarchy(
    frames: fabric.Object[],
    objects: fabric.Object[]
  ): FrameGroup[] {
    const frameChildren = this.collectFrameChildren(frames, objects);
    return this.createFrameGroups(frames, frameChildren);
  }

  private getTopLevelObjects(
    objects: fabric.Object[],
    frames: fabric.Object[]
  ): fabric.Object[] {
    const frameIds = new Set(frames.map(frame => frame.id));
    return this.identifyTopLevelObjects(objects, frames, frameIds);
  }

  /**
   * Collects child objects for each frame, including nested frame hierarchies
   */
  private collectFrameChildren(
    frames: fabric.Object[],
    objects: fabric.Object[]
  ): Map<string, fabric.Object[]> {
    const frameChildren = new Map<string, fabric.Object[]>();
    const frameSet = new Set(frames.map(f => f.id));

    for (const frame of frames) {
      let children: fabric.Object[] = [];

      // Use frame.children if available (as noted in the TODO)
      if (
        isFrameLike(frame) &&
        'children' in frame &&
        Array.isArray(frame.children)
      ) {
        children = frame.children;
      } else {
        // Find all objects that are directly contained by this frame
        children = objects.filter(
          obj =>
            obj !== frame &&
            ((hasFrameMemberBehaviors(obj) && obj.container === frame.id) ||
              frame.containsObject(obj))
        );
      }

      // Separate nested frames from regular objects
      const nestedFrames = children.filter(child => frameSet.has(child.id));
      const regularChildren = children.filter(child => !frameSet.has(child.id));

      // For nested frames, we'll include them in the parent's children list
      // but their own children will be processed when we get to them individually
      frameChildren.set(frame.id, [...nestedFrames, ...regularChildren]);
    }

    return frameChildren;
  }

  /**
   * Creates frame groups with their associated children and center points
   */
  private createFrameGroups(
    frames: fabric.Object[],
    frameChildren: Map<string, fabric.Object[]>
  ): FrameGroup[] {
    return frames.map(frame => {
      const children = frameChildren.get(frame.id) ?? [];
      return {
        frame,
        children,
        center: frame.getCenterPoint(),
      };
    });
  }

  /**
   * Filters frame groups to only include root frames (not nested within other frames)
   * This ensures frame hierarchies are always traversed from the root frame
   */
  private filterRootFrameGroups(
    frameGroups: FrameGroup[],
    allFrames: fabric.Object[]
  ): FrameGroup[] {
    return frameGroups.filter(frameGroup => {
      const frame = frameGroup.frame;

      // Check if this frame is contained within any other frame
      for (const otherFrame of allFrames) {
        if (otherFrame.id !== frame.id && otherFrame.containsObject(frame)) {
          return false; // This frame is nested, exclude it
        }
      }

      // Also check container property for hierarchical containment
      if (hasFrameMemberBehaviors(frame) && frame.container) {
        const containerFrame = allFrames.find(f => f.id === frame.container);
        if (containerFrame) {
          return false; // This frame has a parent frame, exclude it
        }
      }

      return true; // This is a root frame
    });
  }

  /**
   * Identifies objects that are not contained within any frame hierarchy
   */
  private identifyTopLevelObjects(
    objects: fabric.Object[],
    frames: fabric.Object[],
    frameIds: Set<string>
  ): fabric.Object[] {
    return objects.filter(obj => {
      // Not a frame itself and not inside any frame hierarchy
      if (frameIds.has(obj.id)) return false;

      // Check if object is contained in any frame hierarchy
      return !this.isObjectInAnyFrameHierarchy(obj, frames);
    });
  }

  /**
   * Creates and sorts top-level elements by distance from current object
   */
  private createTopLevelElements(
    frameGroups: FrameGroup[],
    topLevelObjects: fabric.Object[],
    currentId: string,
    currentCenter: fabric.Point
  ): TopLevelElement[] {
    const elements: TopLevelElement[] = [];

    // Add frame groups
    for (const unit of frameGroups) {
      if (unit.frame.id !== currentId) {
        const distance = sqrDistanceBetweenPoints(
          currentCenter,
          unit.center.x,
          unit.center.y
        );
        elements.push({
          type: 'frame',
          element: unit,
          distance,
          center: unit.center,
        });
      }
    }

    // Add top-level objects
    for (const obj of topLevelObjects) {
      if (obj.id !== currentId) {
        const center = obj.getCenterPoint();
        const distance = sqrDistanceBetweenPoints(
          currentCenter,
          center.x,
          center.y
        );
        elements.push({
          type: 'object',
          element: obj,
          distance,
          center,
        });
      }
    }

    // Sort by distance from current object
    return elements.sort((a, b) => a.distance - b.distance);
  }

  /**
   * Sorts children within a frame by position (primarily top-to-bottom, then left-to-right)
   */
  private sortChildrenByPosition(children: fabric.Object[]): fabric.Object[] {
    return [...children].sort((a, b) => {
      const aCenter = a.getCenterPoint();
      const bCenter = b.getCenterPoint();

      // Primary sort by Y position, secondary by X
      if (Math.abs(aCenter.y - bCenter.y) > 20) {
        return aCenter.y - bCenter.y;
      }
      return aCenter.x - bCenter.x;
    });
  }

  /**
   * Builds the final order of objects based on proximity and hierarchy
   */
  private buildFinalOrder(
    topLevelElements: TopLevelElement[],
    currentId: string
  ): string[] {
    const finalOrder: string[] = [];
    const allProcessedIds = new Set<string>();

    this.processCurrentObject(currentId, finalOrder, allProcessedIds);
    this.processTopLevelElements(topLevelElements, finalOrder, allProcessedIds);

    return finalOrder;
  }

  private processCurrentObject(
    currentId: string,
    finalOrder: string[],
    allProcessedIds: Set<string>
  ): void {
    const currentObject = this.selectableObjects.find(
      obj => obj.id === currentId
    );

    if (currentObject && isFrameLike(currentObject)) {
      const children = this.getFrameChildren(currentObject);
      this.processFrameRecursively(
        currentObject,
        children,
        finalOrder,
        allProcessedIds
      );
    } else if (!allProcessedIds.has(currentId)) {
      finalOrder.push(currentId);
      allProcessedIds.add(currentId);
    }
  }

  private processTopLevelElements(
    topLevelElements: TopLevelElement[],
    finalOrder: string[],
    allProcessedIds: Set<string>
  ): void {
    for (const element of topLevelElements) {
      if (element.type === 'frame') {
        const { frame, children } = element.element as FrameGroup;
        this.processFrameRecursively(
          frame,
          children,
          finalOrder,
          allProcessedIds
        );
      } else {
        const obj = element.element as fabric.Object;
        if (!allProcessedIds.has(obj.id)) {
          finalOrder.push(obj.id);
          allProcessedIds.add(obj.id);
        }
      }
    }
  }

  /**
   * Recursively processes a frame and all its children, including nested frames
   */
  private processFrameRecursively(
    frame: fabric.Object,
    children: fabric.Object[],
    finalOrder: string[],
    allProcessedIds: Set<string>
  ): void {
    // Add frame if not already processed
    if (!allProcessedIds.has(frame.id)) {
      finalOrder.push(frame.id);
      allProcessedIds.add(frame.id);
    }

    // Always process children, even if frame was already processed
    // (this handles the case where we start with a frame selected)
    const sortedChildren = this.sortChildrenByPosition(children);

    // Process children in their sorted order, handling frames recursively
    for (const child of sortedChildren) {
      if (!allProcessedIds.has(child.id)) {
        if (isFrameLike(child)) {
          // Recursively process nested frame with its children
          const nestedFrameChildren = this.getFrameChildren(child);
          this.processFrameRecursively(
            child,
            nestedFrameChildren,
            finalOrder,
            allProcessedIds
          );
        } else {
          // Add regular object
          finalOrder.push(child.id);
          allProcessedIds.add(child.id);
        }
      }
    }
  }

  /**
   * Helper method to check if an object is contained within any frame hierarchy
   */
  private isObjectInAnyFrameHierarchy(
    obj: fabric.Object,
    frames: fabric.Object[]
  ): boolean {
    // Check direct containment first
    for (const frame of frames) {
      if (frame.containsObject(obj)) {
        return true;
      }
    }

    // Check hierarchical containment through container property
    if (hasFrameMemberBehaviors(obj) && obj.container) {
      // Walk up the container hierarchy
      let currentContainer = obj.container;
      const visited = new Set<string>(); // Prevent infinite loops

      while (currentContainer && !visited.has(currentContainer)) {
        visited.add(currentContainer);

        // Check if this container is one of our frames
        const containerFrame = frames.find(f => f.id === currentContainer);
        if (containerFrame) {
          return true;
        }

        // Move up the hierarchy if the container has FrameMemberBehaviors
        const containerObj = this.selectableObjects.find(
          o => o.id === currentContainer
        );
        if (
          containerObj &&
          hasFrameMemberBehaviors(containerObj) &&
          containerObj.container
        ) {
          currentContainer = containerObj.container;
        } else {
          break;
        }
      }
    }
    return false;
  }

  /**
   * Gets children of a specific frame
   */
  private getFrameChildren(frame: fabric.Object): fabric.Object[] {
    if (
      isFrameLike(frame) &&
      'children' in frame &&
      Array.isArray(frame.children)
    ) {
      return frame.children;
    }

    return this.selectableObjects.filter(
      obj => obj !== frame && this.isObjectInFrame(obj, frame)
    );
  }

  private isObjectInFrame(obj: fabric.Object, frame: fabric.Object): boolean {
    return (
      (hasFrameMemberBehaviors(obj) && obj.container === frame.id) ||
      frame.containsObject(obj)
    );
  }
}
